/**
 * Notifications Page — live admin notifications from API
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MdNotifications,
  MdCheckCircle,
  MdCancel,
  MdSettings,
  MdMarkEmailRead,
} from 'react-icons/md';
import { ClearFiltersButton, PageSkeleton } from '@euroasiann/shared/ui';
import { cn } from '../../lib/utils';
import { authenticatedFetch } from '../../lib/api';

interface PortalNotification {
  _id: string;
  title: string;
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error' | 'budget';
  category?: string;
  link?: string;
  createdAt?: string;
  isRead?: boolean;
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filterType, setFilterType] = useState<string>('all');
  const [filterRead, setFilterRead] = useState<string>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-notifications', 'page', filterRead],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '50' });
      if (filterRead === 'unread') params.set('read', 'false');
      if (filterRead === 'read') params.set('read', 'true');
      const res = await authenticatedFetch(`/api/v1/admin/notifications?${params.toString()}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) {
        throw new Error(json?.error || 'Failed to load notifications');
      }
      return {
        notifications: Array.isArray(json.data) ? (json.data as PortalNotification[]) : [],
        unreadCount: Number(json?.meta?.unreadCount) || 0,
        total: Number(json?.meta?.total) || 0,
      };
    },
    staleTime: 15_000,
  });

  const notifications = data?.notifications ?? [];
  const apiUnread = data?.unreadCount ?? 0;

  const filteredNotifications = useMemo(() => {
    return notifications.filter((notif) => {
      if (filterType !== 'all' && (notif.type || 'info') !== filterType) return false;
      return true;
    });
  }, [notifications, filterType]);

  const listUnread = filteredNotifications.filter((n) => !n.isRead).length;
  const unreadCount = Math.max(apiUnread, listUnread);

  const handleMarkAsRead = async (id: string) => {
    try {
      await authenticatedFetch(`/api/v1/admin/notifications/${id}/read`, { method: 'POST' });
      await queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
    } catch {
      // best-effort
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await authenticatedFetch('/api/v1/admin/notifications/read-all', { method: 'POST' });
      await queryClient.invalidateQueries({ queryKey: ['admin-notifications'] });
    } catch {
      // best-effort
    }
  };

  const getTypeStyles = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 border-emerald-200 dark:border-emerald-800';
      case 'warning':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 border-amber-200 dark:border-amber-800';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 border-red-200 dark:border-red-800';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 border-blue-200 dark:border-blue-800';
    }
  };

  if (isLoading) {
    return <PageSkeleton variant="page" />;
  }

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(var(--foreground))] mb-2">Notifications</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] font-medium">
            Unread alerts and platform activity for your admin account
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={() => void handleMarkAllAsRead()}
              className="flex items-center gap-2 px-4 py-2 bg-[#0088cc] hover:bg-[#0077b3] text-white rounded-xl transition-colors font-semibold text-sm"
            >
              <MdMarkEmailRead className="w-4 h-4" />
              Mark All Read
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--card))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] rounded-xl transition-colors font-semibold text-sm text-[hsl(var(--foreground))]"
          >
            <MdSettings className="w-4 h-4" />
            Settings
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">Total</p>
          <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{data?.total ?? notifications.length}</p>
        </div>
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">Unread</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">{unreadCount}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-amber-500/15 text-amber-600 flex items-center justify-center">
              <MdCancel className="w-5 h-5" />
            </div>
          </div>
        </div>
        <div className="p-6 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[hsl(var(--muted-foreground))] mb-1">Read</p>
              <p className="text-2xl font-bold text-[hsl(var(--foreground))]">
                {Math.max(0, (data?.total ?? notifications.length) - unreadCount)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-600 flex items-center justify-center">
              <MdCheckCircle className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] shadow-sm">
        <div className="flex flex-wrap items-center gap-4">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] text-sm font-medium"
          >
            <option value="all">All Types</option>
            <option value="info">Info</option>
            <option value="success">Success</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
          <select
            value={filterRead}
            onChange={(e) => setFilterRead(e.target.value)}
            className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] text-sm font-medium"
          >
            <option value="all">All Notifications</option>
            <option value="unread">Unread Only</option>
            <option value="read">Read Only</option>
          </select>
          <ClearFiltersButton
            visible={filterType !== 'all' || filterRead !== 'all'}
            onClick={() => {
              setFilterType('all');
              setFilterRead('all');
            }}
            variant="toolbar"
          />
        </div>
      </div>

      {error ? (
        <div className="p-8 text-center text-sm text-red-600 font-medium border border-red-200 rounded-xl bg-red-50">
          {(error as Error).message}
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="p-12 text-center rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))]">
          <MdNotifications className="w-12 h-12 text-[hsl(var(--muted-foreground))] mx-auto mb-4 opacity-40" />
          <p className="text-lg font-semibold text-[hsl(var(--muted-foreground))]">No notifications found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notif) => (
            <div
              key={notif._id}
              className={cn(
                'p-5 rounded-xl border shadow-sm transition-all',
                notif.isRead
                  ? 'bg-[hsl(var(--card))] border-[hsl(var(--border))]'
                  : 'bg-[#0088cc]/5 border-[#0088cc]/25'
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <button
                  type="button"
                  className="flex-1 text-left"
                  onClick={() => {
                    if (!notif.isRead) void handleMarkAsRead(notif._id);
                    if (notif.link) navigate(notif.link);
                  }}
                >
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span
                      className={cn(
                        'px-2.5 py-0.5 text-[10px] font-bold rounded-full border uppercase tracking-wide',
                        getTypeStyles(notif.type || 'info')
                      )}
                    >
                      {notif.type || 'info'}
                    </span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      {notif.category || 'General'}
                    </span>
                    {!notif.isRead && <span className="w-2 h-2 bg-[#0088cc] rounded-full" />}
                  </div>
                  <h3 className="text-base font-semibold text-[hsl(var(--foreground))] mb-1">
                    {notif.title}
                  </h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mb-2">{notif.message}</p>
                  {notif.createdAt && (
                    <p className="text-xs text-[hsl(var(--muted-foreground))]">
                      {new Date(notif.createdAt).toLocaleString()}
                    </p>
                  )}
                </button>
                {!notif.isRead && (
                  <button
                    type="button"
                    onClick={() => void handleMarkAsRead(notif._id)}
                    className="p-2 rounded-lg bg-[#0088cc]/10 text-[#0088cc] hover:bg-[#0088cc]/20 transition-colors shrink-0"
                    title="Mark as read"
                  >
                    <MdCheckCircle className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
